import { FC } from 'react';
import { Identifier, ReferenceManyField, TextField, useCreatePath } from 'react-admin';

import {
  GetB2bIndividualOperationsOneResponse,
  GetB2bIndividualOperationsQuery,
  getB2bIndividualOperationsSortColumns,
} from '@Common/types/bo/b2b/individual_operations';

import {
  b2bIndividualOperationInitiatorChoices,
  b2bIndividualOperationTypeChoices,
} from '@sh4res/bo-common/choices/individual_operations';
import { b2bOrderStatusChoices } from '@sh4res/bo-common/choices/orders';
import { planCategoryChoices } from '@sh4res/bo-common/choices/plans';
import { DatagridTyped, EmptyScreen, Pagination } from '@sh4res/bo-common/components';
import { IdField, SelectChipField, UTCDateField } from '@sh4res/bo-common/fields';
import { EmptyFolderSvg } from '@sh4res/bo-common/graphics';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const testId = 'BoExternalB2bRecurringInvestmentLinkedOperations';

const defaultSort: RaSort<GetB2bIndividualOperationsQuery> = {
  field: 'execution_date',
  order: 'DESC',
};

const displayedColumns: (keyof GetB2bIndividualOperationsOneResponse)[] = [
  'id',
  'initiator',
  'equipment',
  'type',
  'reference_period',
  'execution_date',
  'status',
];

const NoLinkedOperations = () => (
  <EmptyScreen
    label="resources.bo_common.b2b.recurring_investments.show.linked_operations_empty"
    svg={<EmptyFolderSvg />}
  />
);

/**
 * The individual operations generated by a recurring investment, one per execution.
 */
export const B2bRecurringInvestmentLinkedOperations: FC = () => {
  const resourceBasePath = useResourceBasePath();
  const createPath = useCreatePath();

  // Open the operation in a new window so the user keeps the recurring investment page
  const openOperation = (id: Identifier) => {
    const path = createPath({
      resource: `${resourceBasePath}individual_operations`,
      type: 'show',
      id,
    });
    window.open(`#${path}`, '_blank', 'noopener,noreferrer');
    return false as const;
  };

  const columns: ColumnList<GetB2bIndividualOperationsOneResponse> = {
    id: (
      <IdField
        reference={`${resourceBasePath}individual_operations`}
        data-testid={`${testId}_IdField`}
      />
    ),
    initiator: (
      <SelectChipField
        choices={b2bIndividualOperationInitiatorChoices}
        data-testid={`${testId}_InitiatorField`}
      />
    ),
    equipment: (
      <SelectChipField choices={planCategoryChoices} data-testid={`${testId}_EquipmentField`} />
    ),
    type: (
      <SelectChipField
        choices={b2bIndividualOperationTypeChoices}
        data-testid={`${testId}_TypeField`}
      />
    ),
    reference_period: <TextField data-testid={`${testId}_ReferencePeriodField`} />,
    execution_date: (
      <UTCDateField
        showTime={!isBoExternal()}
        emptyText="-"
        data-testid={`${testId}_ExecutionDateField`}
      />
    ),
    status: (
      <SelectChipField choices={b2bOrderStatusChoices} data-testid={`${testId}_StatusField`} />
    ),
  } as ColumnList<GetB2bIndividualOperationsOneResponse>;

  return (
    <ReferenceManyField
      data-testid={`${testId}_ReferenceManyField`}
      reference={`${resourceBasePath}individual_operations`}
      target="b2b_recurring_investment_id"
      sort={defaultSort}
      perPage={10}
      pagination={<Pagination />}>
      <DatagridTyped<GetB2bIndividualOperationsOneResponse>
        rowClick={openOperation}
        sortableColumns={getB2bIndividualOperationsSortColumns}
        columnRegistry={columns}
        displayedColumns={displayedColumns}
        bulkActionButtons={false}
        empty={<NoLinkedOperations />}
      />
    </ReferenceManyField>
  );
};
